/**
 * Loyalty card codes (specs/006-loyalty research R1).
 *
 * 10 random Crockford base32 symbols (50 bits) and one mod-37 check symbol,
 * printed as `XXXX-XXXX-XXX` and carried in the QR without dashes.
 *
 * WHY MOD 37. 37 is prime and neither 32 nor 31 is a multiple of it, so
 * changing any one symbol, or swapping two neighbouring symbols, always changes
 * the remainder. Every such mistake is caught rather than crediting someone
 * else's card.
 *
 * WHY NO `* ~ $ = U` EVER APPEARS. Mod 37 has five check values beyond the 32
 * base32 symbols. The generator draws again whenever a code's check would be
 * one of them, so the check symbol on every printed card is something a
 * cashier can type. A mistyped code still fails the mod-37 test, whatever it
 * lands on.
 *
 * Pure apart from `generateCode` (crypto randomness) and `codeDigest` (SHA-256).
 */

/** Crockford base32: no I, L, O or U. */
export const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const CHECK_EXTRA = "*~$=U";
const CHECK_ALPHABET = ALPHABET + CHECK_EXTRA;

export const DATA_LENGTH = 10;
export const CODE_LENGTH = DATA_LENGTH + 1;

function valueOf(data: string): number | null {
  // 10 symbols × 5 bits = 50 bits, inside a double's exact 53.
  let value = 0;
  for (const symbol of data) {
    const digit = ALPHABET.indexOf(symbol);
    if (digit < 0) return null;
    value = value * 32 + digit;
  }
  return value;
}

/** The check symbol for 10 data symbols. May be one of `* ~ $ = U`. */
export function checkSymbol(data: string): string {
  const value = valueOf(data);
  if (value === null || data.length !== DATA_LENGTH) {
    throw new Error(`Not ${DATA_LENGTH} base32 symbols: ${data}`);
  }
  return CHECK_ALPHABET[value % 37]!;
}

/** True for an already-normalized 11-symbol code whose check symbol is right. */
export function isValidCode(code: string): boolean {
  if (code.length !== CODE_LENGTH) return false;
  const data = code.slice(0, DATA_LENGTH);
  if (valueOf(data) === null) return false;
  return checkSymbol(data) === code[DATA_LENGTH];
}

/**
 * What a person typed, as a code: upper case, no dashes or spaces, and the
 * letters people confuse read as the digits they look like (O→0, I/L→1).
 * Null if anything else is outside the alphabet.
 */
export function normalizeCode(input: string): string | null {
  const cleaned = input
    .toUpperCase()
    .replace(/[\s-]/g, "")
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1");
  if (cleaned === "") return null;
  for (const symbol of cleaned) if (!ALPHABET.includes(symbol)) return null;
  return cleaned;
}

export type ParsedCard =
  | { ok: true; code: string }
  | { ok: false; reason: "characters" | "length" | "check" };

/**
 * A scanned card URL (`…/c/<code>`), a USB scanner's keystrokes, or a typed
 * short code, reduced to a verified code or the reason it is not one.
 */
export function parseCardInput(input: string): ParsedCard {
  let text = input.trim();
  if (text.includes("/")) {
    text = text.split(/[?#]/)[0] ?? "";
    text = text.replace(/\/+$/, "");
    text = text.slice(text.lastIndexOf("/") + 1);
  }

  const code = normalizeCode(text);
  if (code === null) return { ok: false, reason: "characters" };
  if (code.length !== CODE_LENGTH) return { ok: false, reason: "length" };
  if (!isValidCode(code)) return { ok: false, reason: "check" };
  return { ok: true, code };
}

/** `K7M2QX9PAB3` → `K7M2-QX9P-AB3`. */
export function formatCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8)}`;
}

/**
 * A new code from the platform's cryptographic randomness — never
 * `Math.random`, never a counter. Draws again when the check symbol would be
 * one a cashier cannot type.
 */
export function generateCode(random: (bytes: Uint8Array<ArrayBuffer>) => Uint8Array = defaultRandom): string {
  for (;;) {
    const bytes = random(new Uint8Array(new ArrayBuffer(DATA_LENGTH)));
    // A byte's low 5 bits are uniform over 0..31, so no symbol is favoured.
    let data = "";
    for (const byte of bytes) data += ALPHABET[byte & 31];
    const check = checkSymbol(data);
    if (ALPHABET.includes(check)) return data + check;
  }
}

function defaultRandom(bytes: Uint8Array<ArrayBuffer>): Uint8Array {
  return crypto.getRandomValues(bytes);
}

/**
 * First 16 bytes of SHA-256(code), as hex: what the till's replica holds in
 * place of the code itself (research R7). A dumped tablet gives nobody a code
 * they could print.
 */
export async function codeDigest(code: string): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(code));
  return Array.from(new Uint8Array(hash).slice(0, 16), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
